/**
 * FooterBannerCard
 *
 * Displays footer banners (isFooterBanner = true) from the home-screen-images API
 * in the KPI / trust-card area at the bottom of the home page.
 *
 * The image fills the entire card (like the hero banner). Title, subtitle and an
 * optional CTA button are overlaid on a dark gradient scrim at the bottom.
 *
 * - If no footer banners are active it renders nothing (null).
 * - Supports all the same link types as the hero carousel:
 *   INTERNAL → React Router <Link>
 *   EXTERNAL → <a href> in new tab
 *   KEYWORD  → buildSearchUrl with the user's current city
 */
import React from "react";
import { Box, Typography, Button, Grid } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HomeScreenImageItem } from "../../api-data/service-provider/home-screen-images";
import { useLocation } from "../../context/NewLocationContext";
import { buildSearchUrl } from "../../utils/urlHelpers";
import allServicesIllustration from "../../assets/Allservices.webp";

interface Props {
  banners: HomeScreenImageItem[];
  isMobile?: boolean;
}

const FooterBannerCard: React.FC<Props> = ({ banners, isMobile = false }) => {
  const { t } = useTranslation();
  const { selectedCity, selectedLocality } = useLocation();

  // Shared card height
  const cardHeight = isMobile ? 160 : 200;

  const resolveCta = (banner: HomeScreenImageItem): { url: string; type: string } | null => {
    if (!banner?.linkType) return null;
    const type = banner.linkType;
    if (type === "KEYWORD" && banner.effectConfig?.linkKeyword) {
      return { url: buildSearchUrl(selectedCity, selectedLocality, banner.effectConfig.linkKeyword), type };
    }
    if ((type === "INTERNAL" || type === "EXTERNAL") && banner.linkUrl) {
      return { url: banner.linkUrl, type };
    }
    return null;
  };

  const renderCta = (banner: HomeScreenImageItem | null, ctaLabel: string | null) => {
    const cta = banner ? resolveCta(banner) : null;
    const label = ctaLabel || t("home.browseServices");
    const btnSx = {
      mt: 1,
      pointerEvents: "auto" as const,
      fontWeight: 700,
      borderRadius: "100px",
      textTransform: "none" as const,
      fontSize: isMobile ? 12 : 14,
      px: isMobile ? 2 : 2.5,
    };

    if (!cta) {
      return (
        <Button
          variant="contained"
          color="primary"
          size={isMobile ? "small" : "medium"}
          component={Link}
          to="/all-service-categories"
          endIcon={<ArrowForwardIcon sx={{ fontSize: isMobile ? "14px !important" : "16px !important" }} />}
          sx={btnSx}
        >
          {label}
        </Button>
      );
    }

    return cta.type === "EXTERNAL" ? (
      <Button
        variant="contained"
        color="primary"
        size={isMobile ? "small" : "medium"}
        href={cta.url}
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<ArrowForwardIcon sx={{ fontSize: isMobile ? "14px !important" : "16px !important" }} />}
        sx={btnSx}
      >
        {label}
      </Button>
    ) : (
      <Button
        variant="contained"
        color="primary"
        size={isMobile ? "small" : "medium"}
        component={Link}
        to={cta.url}
        endIcon={<ArrowForwardIcon sx={{ fontSize: isMobile ? "14px !important" : "16px !important" }} />}
        sx={btnSx}
      >
        {label}
      </Button>
    );
  };

  // Normalise: always at least one entry (fallback to static content)
  const items = banners.length > 0 ? banners : [null];

  return (
    <Grid
      container
      spacing={2}
      sx={{ ...(isMobile ? { mt: 3 } : { mb: 7 }) }}
    >
      {items.map((banner, idx) => {
        const title    = banner?.localized?.title    ?? banner?.title    ?? t("home.trustBannerTitle");
        const subtitle = banner?.localized?.subtitle ?? banner?.subtitle ?? (isMobile ? t("home.trustBannerSubtitle") : t("home.trustBannerSubtitleLong"));
        const ctaLabel = banner?.localized?.ctaLabel ?? banner?.ctaLabel ?? null;
        const imgSrc   = banner?.imageUrl ?? allServicesIllustration;

        return (
          <Grid size={{ xs: 12, md: 6 }} key={banner?.id ?? idx}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: cardHeight,
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            {/* Full-bleed background image */}
            <Box
              component="img"
              src={imgSrc}
              alt={title}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />

            {/* Dark gradient scrim + text / CTA overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0) 100%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-end",
                px: isMobile ? 2 : 3,
                pb: isMobile ? 2 : 2.5,
                gap: 0.25,
                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: isMobile ? 15 : 20,
                  color: "common.white",
                  lineHeight: 1.2,
                  textShadow: "0 1px 4px rgba(0,0,0,0.4)",
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  fontSize: isMobile ? 12 : 13.5,
                  color: "rgba(255,255,255,0.88)",
                  lineHeight: 1.4,
                  textShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              >
                {subtitle}
              </Typography>

              {renderCta(banner, ctaLabel)}
            </Box>
          </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default FooterBannerCard;
